import { FlaskConical, Globe2, Cog, Lightbulb, ClipboardCheck, Search, Hammer, MessageCircleHeart } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurable external Student Login link (set REACT_APP_STUDENT_LOGIN_URL in frontend/.env)
export const STUDENT_LOGIN_URL = process.env.REACT_APP_STUDENT_LOGIN_URL || "";

export const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/courses", label: "Courses", testid: "nav-courses" },
  { to: "/approach", label: "Our Approach", testid: "nav-approach" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export const COURSES = [
  {
    slug: "science-inquiry",
    title: "Science Inquiry",
    color: "bg-mint",
    icon: FlaskConical,
    pbl: true,
    short: "Taught through project-based learning: ask big questions about the natural world, then design, test and build to find out.",
    description: [
      "Our comprehensive Science Programme is designed for students from Year 5 to Year 10, offering a structured six-year learning pathway informed by the science curricula of New Zealand, Australia, and the United States.",
      "The programme brings together Biology, Chemistry, Physics, and Earth and Space Science through interconnected learning topics, hands-on investigations, and real-world applications. Each two-hour session encourages students to explore scientific concepts, develop evidence-based reasoning, and build independent inquiry skills.",
    ],
    focus: ["Asking testable questions", "Designing fair experiments", "Collecting and reading data", "Presenting a finished project"],
  },
  {
    slug: "social-studies",
    title: "Social Studies",
    color: "bg-blush",
    icon: Globe2,
    pbl: true,
    short: "Taught through project-based learning: investigate people, places and change in Aotearoa and around the world.",
    description: [
      "Our Social Studies Programme helps students understand how people, communities, and societies have developed, interacted, and changed over time.",
      "Integrating History, Geography, Civics, Economics, and Cultural Studies, the programme explores human experiences, historical events, cultural diversity, social systems, and global connections.",
      "Through historical investigations, geographical inquiry, primary-source analysis, and real-world case studies, students develop the ability to understand multiple perspectives, recognise connections between past and present, and become thoughtful, informed global citizens.",
    ],
    focus: ["Exploring perspectives", "Using sources and maps", "Communities and citizenship", "Taking informed action through a project"],
  },
  {
    slug: "engineering",
    title: "Engineering",
    color: "bg-sky",
    icon: Cog,
    pbl: true,
    short: "Taught through project-based learning: design, build and test solutions to real problems, from sturdy structures to simple machines.",
    description: [
      "Our Engineering Programme transforms creative ideas into practical solutions through hands-on design challenges, experimentation, and project-based learning.",
      "Using the engineering design process, students learn to identify problems, investigate possibilities, develop prototypes, test solutions, and improve their designs through reflection and iteration.",
      "Combining engineering principles, technology, mathematics, and creative thinking, the programme encourages students to learn through making, embrace challenges, collaborate effectively, and develop the confidence to solve real-world problems.",
    ],
    focus: ["The engineering design process", "Structures and forces", "Simple machines and mechanisms", "Prototyping and improving"],
  },
  {
    slug: "critical-thinking",
    title: "Critical Thinking",
    color: "bg-lavender",
    icon: Lightbulb,
    pbl: true,
    short: "Taught through project-based learning: weigh ideas, spot weak arguments and build reasoning you can stand behind.",
    description: [
      "Our six-year Critical Thinking Programme offers learning sessions across six interconnected themes: Society & Culture, Science & Technology, Environment & Sustainability, Economics & Globalisation, Ethics & Philosophy, and Media & Information.",
      "Rather than following a fixed list of topics, the programme draws on current events, emerging challenges, and real-world issues, with discussions and investigations carefully adapted to students' ages and learning levels.",
      "Students progressively learn to apply thinking tools, logical reasoning, evidence evaluation, and decision-making frameworks to examine different perspectives, challenge assumptions, and develop well-supported conclusions.",
      "Our goal is to nurture curious, informed, and independent thinkers who can confidently navigate an increasingly complex and rapidly changing world.",
    ],
    focus: ["Questioning claims", "Logic and reasoning", "Problem solving", "Creative, open-ended thinking"],
  },
];

export const PROCESS = [
  { title: "Pre-Assessment", color: "bg-lavender", icon: ClipboardCheck, text: "We start by finding out what each learner already knows and what they are curious about." },
  { title: "Investigate", color: "bg-mint", icon: Search, text: "Learners ask questions, research, test ideas and gather evidence of their own." },
  { title: "Practice", color: "bg-lemon", icon: Hammer, text: "New skills are applied through hands-on tasks and projects they actually build." },
  { title: "Feedback", color: "bg-blush", icon: MessageCircleHeart, text: "Regular, specific feedback helps learners reflect, improve and plan the next step." },
];

// Example project themes (illustrative, not real student work)
export const PROJECTS = [
  { title: "Build a water filter", course: "Science Inquiry", color: "bg-mint", text: "Which materials clean muddy water best? Test, measure and refine a filter design." },
  { title: "Care for a local stream", course: "Science + Social Studies", color: "bg-lavender", text: "Test water quality, learn the stream's story with the community and propose a plan to protect it." },
  { title: "Map my community", course: "Social Studies", color: "bg-lemon", text: "Investigate a local place: how it has changed, who it serves and what could make it better." },
  { title: "Fact or fake?", course: "Critical Thinking", color: "bg-blush", text: "Examine a viral claim, trace its sources and present a reasoned verdict." },
  { title: "Design a greener school", course: "Cross-curricular", color: "bg-mint", text: "Combine science, data and persuasion to pitch a sustainability idea." },
];

export const WHY = [
  { title: "Questions, not memorising", color: "bg-lavender", text: "Kids learn by asking questions, not memorising. Curiosity leads every lesson." },
  { title: "Skills for the future", color: "bg-mint", text: "Skills for the future, not just exam marks: thinking, creating, collaborating and communicating." },
  { title: "Hands-on projects", color: "bg-lemon", text: "Hands-on projects they actually build, so learning sticks and feels real." },
];

export const LEARNING_MODES = [
  { value: "online", label: "Online courses" },
  { value: "in-person", label: "At MUCCI locations" },
];

export const ROLES = [
  { value: "parent", label: "Parent / caregiver" },
  { value: "student", label: "Student" },
  { value: "homeschool", label: "Homeschooling family" },
  { value: "other", label: "Other" },
];

export const YEAR_LEVELS = Array.from({ length: 6 }, (_, i) => i + 5);

// Online course timetable. Fill in real sessions here, e.g.
// { course: "science-inquiry", years: "Years 5–6", day: "Tuesday", time: "4:00–5:00 pm", note: "" }
// While empty, the timetable page lists each course with "To be confirmed".
export const ONLINE_TIMETABLE = [];

// MUCCI locations shown on the Locations page. Add real ones here, e.g.
// { name: "MUCCI Academy Central", address: "Full street address, Suburb, City", note: "" }
// While empty, the page shows a New Zealand overview map and a "coming soon" notice.
export const LOCATIONS = [];
